import { useState } from 'react'
import { useSetOutcome } from '../../api/queries/dealMutations'
import { userFacingMessage } from '../../api/errors'
import { Button } from '../../ui/Button'
import { Select } from '../../ui/Select'
import { formatDate } from '../../lib/format'
import type { DealDetail } from '../../api/adapters/dealDetail'
import styles from './DealPanel.module.css'

/**
 * Won, lost, or reopened.
 *
 * A win or a loss is usually settled off the call, so it is the user's to state
 * and is never inferred. Setting either one also takes the deal out of the
 * active pipeline — that is the backend's behaviour, said plainly here so the
 * card disappearing from the board is not a surprise.
 */
const LOST_REASONS = [
  { value: '', label: 'Reason (optional)' },
  { value: 'Went with competitor', label: 'Went with competitor' },
  { value: 'No budget', label: 'No budget' },
  { value: 'No decision', label: 'No decision' },
  { value: 'Went quiet', label: 'Went quiet' },
  { value: 'Timing', label: 'Timing' },
]

export function OutcomeBar({ deal }: { deal: DealDetail }) {
  const [askingReason, setAskingReason] = useState(false)
  const [reason, setReason] = useState('')
  const outcome = useSetOutcome(deal.id)

  if (deal.outcome) {
    return (
      <div className={`${styles.outcome} ${deal.outcome === 'won' ? styles.won : styles.lost}`}>
        <span>
          {deal.outcome === 'won' ? 'Won' : 'Lost'}
          {deal.outcomeAt ? ` · ${formatDate(deal.outcomeAt)}` : ''}
          {deal.lostReason ? ` · ${deal.lostReason}` : ''}
          <span className={styles.outcomeNote}> · out of the active pipeline</span>
        </span>
        <Button
          disabled={outcome.isPending}
          onClick={() => outcome.mutate({ outcome: null, lost_reason: null })}
        >
          {outcome.isPending ? 'Reopening…' : 'Reopen'}
        </Button>
        {outcome.isError ? (
          <p className={styles.failed} role="alert">
            This deal was not reopened. {userFacingMessage(outcome.error)}
          </p>
        ) : null}
      </div>
    )
  }

  return (
    <div>
      <div className={styles.outcomeActions}>
        <Button
          className={styles.markWon}
          disabled={outcome.isPending}
          onClick={() => outcome.mutate({ outcome: 'won', lost_reason: null })}
        >
          Mark won
        </Button>
        <Button className={styles.markLost} disabled={outcome.isPending} onClick={() => setAskingReason(true)}>
          Mark lost
        </Button>
      </div>

      {askingReason ? (
        <div className={styles.lostRow}>
          <Select
            label="Why was it lost?"
            hideLabel
            options={LOST_REASONS}
            value={reason}
            onChange={(event) => setReason(event.target.value)}
          />
          <Button
            className={styles.markLost}
            disabled={outcome.isPending}
            onClick={() =>
              outcome.mutate(
                { outcome: 'lost', lost_reason: reason || null },
                { onSuccess: () => setAskingReason(false) },
              )
            }
          >
            {outcome.isPending ? 'Saving…' : 'Confirm lost'}
          </Button>
          <Button disabled={outcome.isPending} onClick={() => setAskingReason(false)}>
            Cancel
          </Button>
        </div>
      ) : null}

      {outcome.isError ? (
        <p className={styles.failed} role="alert">
          The outcome was not saved. {userFacingMessage(outcome.error)}
        </p>
      ) : null}
    </div>
  )
}
